import { TrendingUp, Clock, DollarSign, ArrowRight, Target, Zap, CheckCircle2 } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';

const caseStudies = [
  {
    title: 'Global FinTech Achieves 10x Transaction Capacity',
    industry: 'Financial Services',
    client: 'Leading Digital Bank',
    challenge: 'Processing bottlenecks causing $2M+ daily revenue loss during peak hours. Legacy infrastructure couldn\'t scale.',
    solution: 'Rebuilt core banking platform with microservices architecture, implemented real-time data processing, and migrated to cloud infrastructure.',
    description: 'We transformed their legacy banking system into a modern, cloud-native platform that processes millions of transactions daily with sub-second latency.',
    duration: '6 months',
    metrics: [
      { icon: TrendingUp, label: '10x Capacity', sublabel: 'Transaction throughput', color: 'text-emerald-600', bg: 'bg-emerald-50' },
      { icon: Clock, label: '87% Faster', sublabel: 'Processing speed', color: 'text-blue-600', bg: 'bg-blue-50' },
      { icon: DollarSign, label: '$15M ARR', sublabel: 'New revenue unlocked', color: 'text-purple-600', bg: 'bg-purple-50' },
    ],
    results: [
      'Zero downtime during migration',
      '99.99% system uptime achieved',
      'SOC 2 & PCI DSS compliant'
    ],
    techStack: ['React', 'Node.js', 'PostgreSQL', 'AWS', 'Kubernetes'],
    image: 'https://images.unsplash.com/photo-1730382624709-81e52dd294d4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHN1Y2Nlc3MlMjBncmFwaHxlbnwxfHx8fDE3Njc3NzgyMjd8MA&ixlib=rb-4.1.0&q=80&w=1080',
  },
  {
    title: 'E-Commerce Giant Handles Black Friday at 15x Scale',
    industry: 'Retail & E-Commerce',
    client: 'Top 10 Online Retailer',
    challenge: 'Platform crashed during previous Black Friday, losing $8M in sales. Needed bulletproof scalability for peak shopping season.',
    solution: 'Implemented auto-scaling cloud infrastructure, optimized database queries, built CDN strategy, and load-tested for 20x expected traffic.',
    description: 'Built a battle-tested e-commerce platform that seamlessly handled record-breaking Black Friday traffic with perfect uptime and lightning-fast performance.',
    duration: '4 months',
    metrics: [
      { icon: TrendingUp, label: '15x Scale', sublabel: 'Traffic handled', color: 'text-emerald-600', bg: 'bg-emerald-50' },
      { icon: Clock, label: '100% Uptime', sublabel: 'Zero downtime', color: 'text-blue-600', bg: 'bg-blue-50' },
      { icon: DollarSign, label: '$24M Sales', sublabel: 'Black Friday revenue', color: 'text-purple-600', bg: 'bg-purple-50' },
    ],
    results: [
      '1.2s average page load time',
      '45% increase in conversion rate',
      'Handled 500K concurrent users'
    ],
    techStack: ['Next.js', 'MongoDB', 'Redis', 'AWS', 'CloudFront'],
    image: 'https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwaW5ub3ZhdGlvbnxlbnwxfHx8fDE3Njc3MTIyNTV8MA&ixlib=rb-4.1.0&q=80&w=1080',
  },
  {
    title: 'Healthcare Network Saves 12,000 Hours Annually',
    industry: 'Healthcare Technology',
    client: 'Multi-State Hospital System',
    challenge: 'Manual patient data entry across 75 facilities causing errors, delays, and staff burnout. HIPAA compliance concerns.',
    solution: 'Developed unified patient records platform with AI-assisted data entry, automated workflows, and enterprise-grade security.',
    description: 'Created a HIPAA-compliant healthcare data platform that unified patient records across 75+ facilities, dramatically improving care coordination and staff efficiency.',
    duration: '8 months',
    metrics: [
      { icon: TrendingUp, label: '65% Faster', sublabel: 'Patient processing', color: 'text-emerald-600', bg: 'bg-emerald-50' },
      { icon: Clock, label: '12K Hours', sublabel: 'Saved annually', color: 'text-blue-600', bg: 'bg-blue-50' },
      { icon: DollarSign, label: '$18M Saved', sublabel: 'Operational costs', color: 'text-purple-600', bg: 'bg-purple-50' },
    ],
    results: [
      '92% reduction in data entry errors',
      'Full HIPAA & HITECH compliance',
      '98% staff satisfaction score'
    ],
    techStack: ['React Native', 'Python', 'PostgreSQL', 'Azure', 'FHIR'],
    image: 'https://images.unsplash.com/photo-1531498860502-7c67cf02f657?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGRldmVsb3BtZW50JTIwY29kZXxlbnwxfHx8fDE3Njc2NzY1OTB8MA&ixlib=rb-4.1.0&q=80&w=1080',
  },
];

export function CaseStudies() {
  return (
    <section id="case-studies" className="py-20 md:py-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-purple-600/5 to-transparent rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20 max-w-4xl mx-auto animate-fadeInUp">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 border border-purple-100 mb-6">
            <Target size={16} className="text-purple-600" />
            <span className="text-sm text-purple-700 font-semibold uppercase tracking-wider">
              Proven Results
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-extrabold mb-6">
            <span className="gradient-text-premium">Real Clients.</span>
            <br />
            <span className="text-gray-900">Real Impact. Real ROI.</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            See how we've helped industry leaders achieve breakthrough results and transform their businesses with cutting-edge technology solutions.
          </p>
        </div>

        {/* Case Studies */}
        <div className="space-y-12">
          {caseStudies.map((study, index) => (
            <div
              key={index}
              className="premium-card rounded-3xl overflow-hidden animate-fadeInUp"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="grid lg:grid-cols-5 gap-0">
                {/* Image */}
                <div className="lg:col-span-2 relative min-h-[300px] lg:min-h-[500px]">
                  <ImageWithFallback
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/10 to-transparent" />
                  
                  {/* Floating badge */}
                  <div className="absolute top-6 left-6 bg-white rounded-xl shadow-premium px-4 py-2">
                    <div className="text-xs text-gray-600 mb-0.5">Project Duration</div>
                    <div className="text-lg font-bold text-gray-900">{study.duration}</div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:col-span-3 p-8 lg:p-12">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-4 py-1.5 text-xs font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full uppercase tracking-wider">
                      {study.industry}
                    </span>
                    <span className="text-sm text-gray-600 font-medium">{study.client}</span>
                  </div>
                  
                  <h3 className="text-3xl md:text-4xl font-extrabold mb-6 text-gray-900">
                    {study.title}
                  </h3>

                  {/* Challenge & Solution */}
                  <div className="space-y-4 mb-8">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Zap size={18} className="text-red-500" />
                        <span className="text-sm font-bold text-gray-900 uppercase tracking-wider">Challenge</span>
                      </div>
                      <p className="text-gray-600 leading-relaxed pl-6">{study.challenge}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Target size={18} className="text-blue-600" />
                        <span className="text-sm font-bold text-gray-900 uppercase tracking-wider">Solution</span>
                      </div>
                      <p className="text-gray-600 leading-relaxed pl-6">{study.solution}</p>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="grid sm:grid-cols-3 gap-6 mb-8 pb-8 border-b-2 border-gray-100">
                    {study.metrics.map((metric, idx) => {
                      const Icon = metric.icon;
                      return (
                        <div key={idx} className={`${metric.bg} rounded-xl p-4`}>
                          <Icon size={24} className={`${metric.color} mb-2`} />
                          <div className={`text-2xl font-bold ${metric.color} mb-1`}>
                            {metric.label}
                          </div>
                          <div className="text-xs text-gray-600 font-medium">{metric.sublabel}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Key Results */}
                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Key Results</h4>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle2 size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700 font-medium">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tech Stack */}
                  <div className="mb-8">
                    <h4 className="text-xs text-gray-600 font-semibold uppercase tracking-wider mb-3">Tech Stack</h4>
                    <div className="flex flex-wrap gap-2">
                      {study.techStack.map((tech, idx) => (
                        <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => {
                      const element = document.getElementById('contact');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="group flex items-center gap-2 text-blue-600 font-bold hover:gap-3 transition-all"
                  >
                    <span>Get Similar Results</span>
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center animate-fadeInUp">
          <p className="text-lg text-gray-600 mb-6">
            Ready to become our next success story?
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            style={{backgroundColor:"#5e077a"}}
            className="px-10 py-5 text-white rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-blue-500/50 hover:-translate-y-1 transition-all inline-flex items-center gap-3"
          >
            Start Your Transformation
            <ArrowRight size={22} />
          </button>
        </div>
      </div>
    </section>
  );
}